import { useDispatch, useSelector } from "react-redux";
import { updateConfig as globalUpdateConfig } from "../../../store/slices/Global";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { deleteYearlyRecap, viewYearlyRecap } from "./api";
import { IconText } from "@wac-ui-dashboard/wac_component_library";
import { useGetRecapBasicDataQuery } from "../../../store/queries/Admin";
import { current } from "@reduxjs/toolkit";
import {
  cms,
  useGetYearlyRecapListDataQuery,
  useUpdateRecapStatusMutation,
  useUpdateYearlyRecapTableHeadDataMutation,
} from "../../../store/queries/CMS";

const useYearlyRecap = () => {
  let paginationOptions = [
    { label: "10 Per page", value: 10 },
    { label: "20 Per page", value: 20 },
    { label: "30 Per page", value: 30 },
  ];
  const dispatch = useDispatch();
  const { showEditModal } = useSelector((state) => state.global);
  const { data: recapBasicData = {} } = useGetRecapBasicDataQuery();
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [yearlyRecapState, setYearlyRecapState] = useState({
    currentPageSize: 10,
    currentPage: 1,
    search: "",
    sortBy: "",
    sortOrder: "desc",
    itemId: "",
    showModal: false,
    itemData: "",
    imageFile: "",
    imageAliasFile: "",
    isEdit: false,
    showImageModal: false,
    imageData: "",
    tableFeilds: {},
    previewSlide: {
      type: "",
      data: {},
    },
  });
  const {
    data: yearlyRecapList = {},
    isLoading,
    isFetching,
    refetch,
  } = useGetYearlyRecapListDataQuery({
    page_size: yearlyRecapState?.currentPageSize,
    page: yearlyRecapState?.currentPage,
    search: yearlyRecapState?.search,
    sort_by: yearlyRecapState?.sortBy,
    sort_order: yearlyRecapState?.sortOrder,
  });
  const [updateYearlyRecapTableHeadData] =
    useUpdateYearlyRecapTableHeadDataMutation();
  const [updateRecapStatus] = useUpdateRecapStatusMutation();
  useEffect(() => {
    if (yearlyRecapList?.fields) {
      let updatedFields = {
        ...yearlyRecapList?.fields,
        view: {
          label: "Preview",
          sortable: true,
          hidden: false,
          position: 8,
        },
      };
      setYearlyRecapState((prev) => ({
        ...prev,
        tableFeilds: updatedFields,
      }));
    }
    // eslint-disable-next-line
  }, [yearlyRecapList]);
  const handleSearch = (e) => {
    setYearlyRecapState((prev) => ({
      ...prev,
      search: e?.target?.value,
      currentPage: 1,
    }));
  };
  const handleClearClick = () => {
    setYearlyRecapState((prev) => ({
      ...prev,
      search: "",
      currentPage: 1,
    }));
  };
  const handleSort = (label) => {
    if (yearlyRecapState?.sortBy === label) {
      setYearlyRecapState((prev) => ({
        ...prev,
        currentPage: 1,
        sortOrder: prev.sortOrder === "asc" ? "desc" : "asc",
      }));
    } else {
      setYearlyRecapState((prev) => ({
        ...prev,
        currentPage: 1,
        sortBy: label,
        sortOrder: "asc",
      }));
    }
  };
  const handleEditColumnsClick = () => {
    dispatch(
      globalUpdateConfig((state) => {
        state.showEditModal = !state.showEditModal;
      })
    );
  };
  const handleViewPreview = ({ data, type }) => {
    viewYearlyRecap(data?._id).then((res) => {
      if (res?.data?.status_code === 200) {
        setYearlyRecapState((prev) => ({
          ...prev,
          openPreviewModal: true,
          previewSlide: {
            type: type || data?.dynamic_type,
            data: res?.data?.data || {},
          },
        }));
      } else {
        toast.error("Something went wrong");
      }
    });
  };
  const getRow = (field, data) => {
    const rows = {
      common: (field, data) => <p className="pro-mb-0">{data?.[field]}</p>,
      image: (field, data) => {
        return (
          <div className="no-wrap">
            <IconText
              icon={
                <span className="material-symbols-outlined x4">imagesmode</span>
              }
              title={`${"View Photo"}`}
              onClick={() => handleViewImage(data?.image)}
            />
          </div>
        );
      },
      headings: (field, data) => {
        return data[field]?.map((item, index) => (
          <div className="pro-mb-0" key={index}>
            <p className="pro-mb-0">{item}</p>
          </div>
        ));
      },
      heading_position: (field, data) => {
        return (
          recapBasicData?.data?.position?.find(
            (item) => item.id === data?.[field]
          )?.name || "-"
        );
      },
      content_type: (field, data) => {
        return (
          recapBasicData?.data?.type?.find((item) => item.id === data?.[field])
            ?.name || "-"
        );
      },
      alias_image: (field, data) => {
        return (
          <div className="no-wrap">
            <IconText
              icon={
                <span className="material-symbols-outlined x4">imagesmode</span>
              }
              title={`${"View Photo"}`}
              onClick={() => handleViewImage(data?.alias_image)}
            />
          </div>
        );
      },
      status: (field, data) => {
        const isChecked = data?.status?.toString() === "1";
        return (
          <div className="form-check form-switch ">
            <input
              className="form-check-input cursor-pointer"
              type="checkbox"
              role="switch"
              id="flexSwitchCheckDefault"
              checked={isChecked}
              onChange={(e) => handleToggle(data?._id, data)}
            />
          </div>
        );
      },
      view: (field, data) => {
        return (
          <div className="no-wrap">
            <IconText
              icon={
                <span className="material-symbols-outlined x4">visibility</span>
              }
              title={`${"View"}`}
              onClick={() => {
                handleViewPreview({ data, type: data?.dynamic_type });
              }}
            />
          </div>
        );
      },
    };
    return rows?.[field]?.(field, data) ?? rows["common"](field, data);
  };
  const handleToggle = (id, data) => {
    const newStatus = data.status === "1" ? false : true;
    let params = {
      id: data?._id,
      status: newStatus ? 1 : 2,
    };
    updateRecapStatus(params);
    dispatch(
      cms.util.updateQueryData(
        "getYearlyRecapListData",
        {
          page_size: yearlyRecapState?.currentPageSize,
          page: yearlyRecapState?.currentPage,
          search: yearlyRecapState?.search,
          sort_by: yearlyRecapState?.sortBy,
          sort_order: yearlyRecapState?.sortOrder,
        },
        (cacheEntry) => {
          let currentCache = { ...current(cacheEntry) };
          const newResult = currentCache.data.data?.map((res) =>
            res._id === id
              ? {
                  ...res,
                  status: data.status === "1" ? "2" : "1",
                }
              : res
          );
          currentCache = {
            ...currentCache,
            data: {
              ...currentCache.data,
              data: newResult,
            },
          };
          return currentCache;
        }
      )
    );
  };
  const handleEditAction = (id) => {
    const itemId = id?.[0];
    viewYearlyRecap(itemId).then((res) => {
      if (res?.data?.status_code === 200) {
        setYearlyRecapState((prev) => ({
          ...prev,
          itemId: itemId,
          itemData: res?.data?.data,
          showModal: true,
          imageFile: res?.data?.data?.image_full_url ?? "",
          imageAliasFile: res?.data?.data?.alias_image_full_url ?? "",
          isEdit: true,
        }));
      } else {
        toast.error("Something went wrong");
      }
    });
  };
  const handleDeleteModal = (e) => {
    setYearlyRecapState((prev) => ({
      ...prev,
      itemId: e?.[0] || "",
      showDeleteModal: !yearlyRecapState?.showDeleteModal,
    }));
    setIsDeleteLoading(false);
  };
  const handlePagination = (page) => {
    setYearlyRecapState((prev) => ({
      ...prev,
      currentPage: page,
    }));
  };
  const handleDelete = async () => {
    setIsDeleteLoading(true);
    const response = await deleteYearlyRecap(yearlyRecapState?.itemId);
    if (response && response.status === 200) {
      toast.success(`Yearly Recap deleted successfully.`);
      setYearlyRecapState((prev) => ({
        ...prev,
        currentPageSize: 10,
        currentPage: 1,
        itemId: "",
        showDeleteModal: false,
      }));
      setIsDeleteLoading(false);
      refetch();
    } else {
      setYearlyRecapState((prev) => ({
        ...prev,
        itemId: "",
        showDeleteModal: false,
      }));
      setIsDeleteLoading(false);
      toast.error(`Something went wrong !`);
    }
  };
  
  const handlePageSize = ({ value: page_size }) => {
    setYearlyRecapState((prev) => ({
      ...prev,
      currentPageSize: page_size,
      currentPage: 1,
    }));
  };
  const handleModal = () => {
    setYearlyRecapState((prev) => ({
      ...prev,
      imageData: "",
      showModal: !yearlyRecapState?.showModal,
      itemId: "",
      imageFile: "",
      imageAliasFile: "",
      isEdit: false,
      itemData: "",
    }));
  };
  const closeImageModal = () => {
    setYearlyRecapState((prev) => ({
      ...prev,
      showImageModal: false,
      imageData: "",
    }));
  };
  const closePreviewModal = () => {
    setYearlyRecapState((prev) => ({
      ...prev,
      openPreviewModal: false,
      previewSlide: {
        type: "",
        data: {},
      },
    }));
  };
  const handleViewImage = (data) => {
    setYearlyRecapState((prev) => ({
      ...prev,
      showImageModal: true,
      imageData: data ?? "",
    }));
  };
  return {
    isLoading,
    isFetching,
    showEditModal,
    paginationOptions,
    yearlyRecapList,
    yearlyRecapState,
    recapBasicData,
    isDeleteLoading,
    handleDelete,
    handleDeleteModal,
    closeImageModal,
    closePreviewModal,
    setYearlyRecapState,
    handleEditAction,
    handleModal,
    setYearlyRecapState,
    updateYearlyRecapTableHeadData,
    handleEditColumnsClick,
    handlePagination,
    handleClearClick,
    handlePageSize,
    handleSearch,
    handleSort,
    refetch,
    getRow,
  };
};

export default useYearlyRecap;
